import React, { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { UrduInput } from "../../Shared/UrduInput";

const EMPTY_FORM = {
  name: "",
  name_ur: "",
  address: "",
  address_ur: "",
  phone: "",
  latitude: "",
  longitude: "",
};

function PickupPointModal({ isOpen, onClose, onSubmit, editPoint }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      setIsSaving(false);
      return;
    }

    if (editPoint) {
      setFormData({
        name: editPoint.name || "",
        name_ur: editPoint.name_ur || "",
        address: editPoint.address || "",
        address_ur: editPoint.address_ur || "",
        phone: editPoint.phone || "",
        latitude: editPoint.latitude ?? "",
        longitude: editPoint.longitude ?? "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, editPoint]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = "Name is required";
    if (!formData.address.trim()) nextErrors.address = "Address is required";

    const lat = Number(formData.latitude);
    if (formData.latitude === "" || Number.isNaN(lat)) {
      nextErrors.latitude = "Latitude is required";
    } else if (lat < -90 || lat > 90) {
      nextErrors.latitude = "Latitude must be between -90 and 90";
    }

    const lng = Number(formData.longitude);
    if (formData.longitude === "" || Number.isNaN(lng)) {
      nextErrors.longitude = "Longitude is required";
    } else if (lng < -180 || lng > 180) {
      nextErrors.longitude = "Longitude must be between -180 and 180";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSaving) return;

    setIsSaving(true);
    try {
      await onSubmit({
        name: formData.name.trim(),
        name_ur: formData.name_ur.trim(),
        address: formData.address.trim(),
        address_ur: formData.address_ur.trim(),
        phone: formData.phone.trim(),
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const isEditMode = Boolean(editPoint?.id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {isEditMode ? "Edit Pickup Point" : "Add Pickup Point"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name (English) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Lahore"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
                errors.name
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
            <UrduInput
              label="نام (اردو)"
              name="name_ur"
              value={formData.name_ur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address (English) <span className="text-red-500">*</span>
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
              placeholder="12 B/1, Johar town, Lahore"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
                errors.address
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
            />
            {errors.address && (
              <p className="text-red-500 text-sm mt-1">{errors.address}</p>
            )}
            <UrduInput
              label="پتہ (اردو)"
              name="address_ur"
              value={formData.address_ur}
              onChange={handleChange}
              type="textarea"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="03395111199"
              dir="ltr"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                step="any"
                placeholder="31.46252348"
                dir="ltr"
                className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
                  errors.latitude
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
              />
              {errors.latitude && (
                <p className="text-red-500 text-sm mt-1">{errors.latitude}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                step="any"
                placeholder="74.29995846"
                dir="ltr"
                className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
                  errors.longitude
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
              />
              {errors.longitude && (
                <p className="text-red-500 text-sm mt-1">{errors.longitude}</p>
              )}
            </div>
          </div>
          <p className="text-gray-500 text-xs">
            Paste coordinates from Google Maps. The website builds map links from
            these values.
          </p>

          <div className="flex justify-end mt-6 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-reset-filter justify-center"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`btn justify-center ${
                isEditMode ? "btn-edit text-white" : "btn-primary"
              } ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PickupPointModal;
